import styled from '@emotion/styled';
import { colors, fonts, layout } from './theme';

const Root = styled.section`
  background: ${colors.baselineWhite};
  padding: clamp(56px, 8vw, 96px) 0;
`;

const Inner = styled.div`
  max-width: ${layout.maxWidth};
  margin: 0 auto;
  padding: 0 clamp(20px, 4vw, 32px);
  scroll-margin-top: 90px;
`;

const Eyebrow = styled.p`
  font-family: ${fonts.mono};
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${colors.textMuted};
  margin: 0 0 16px;
`;

const Headline = styled.h2`
  font-family: ${fonts.sans};
  font-size: clamp(32px, 3.8vw, 48px);
  line-height: 1.15;
  max-width: 22ch;
  font-weight: 400;
  color: ${colors.coreBlue};
  margin: 0 0 48px;
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: 800px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Card = styled.div`
  border: 1px solid ${colors.borderSubtle};
  border-radius: 8px;
  overflow: hidden;
`;

const CardBar = styled.div`
  height: 6px;
  display: flex;
`;

const CardBarSegment = styled.div<{ flexValue: number; background: string }>`
  flex: ${({ flexValue }) => flexValue};
  background: ${({ background }) => background};
`;

const CARD_BAR_SEGMENTS = {
  web: [
    { flexValue: 4, background: colors.patternBlue1 },
    { flexValue: 3, background: colors.patternBlue2 },
    { flexValue: 2, background: colors.patternBlue3 },
    { flexValue: 1, background: colors.patternBlue4 },
  ],
  ctv: [
    { flexValue: 4, background: colors.patternGreen1 },
    { flexValue: 3, background: colors.patternGreen2 },
    { flexValue: 2, background: colors.patternGreen3 },
    { flexValue: 1, background: colors.patternGreen4 },
  ],
} as const;

const CardBody = styled.div`
  padding: 28px 24px;
`;

const CardTitle = styled.h3`
  font-family: ${fonts.sans};
  font-size: 24px;
  line-height: 1.3;
  font-weight: 400;
  color: ${colors.coreBlue};
  margin: 0 0 12px;
`;

const CardIntro = styled.p`
  font-family: ${fonts.sans};
  font-size: 17px;
  line-height: 1.5;
  color: ${colors.textSecondary};
  margin: 0 0 20px;
`;

const Checklist = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const ChecklistItem = styled.li`
  display: flex;
  gap: 10px;
  font-family: ${fonts.sans};
  font-size: 16px;
  line-height: 1.45;
  color: ${colors.coreBlue};
  padding: 8px 0;

  &::before {
    content: '\\2713';
    color: ${colors.coreBlueDark};
    flex-shrink: 0;
  }
`;

const card1Intro =
  'Inject video almost anywhere to increase publisher yields — editorial pages or display ad slots, portrait or landscape, inline or floating.';
const card1Bullets = [
  'Turn image-led placements into video experiences',
  'Stream advertising, content or curated playlists',
  'Custom branding, end boards and calls to action',
  'Event-level attention, viewability, watch-time and completion reporting',
  'Lightweight player initiation in under 50ms',
];

const card2Intro =
  "Extend Pixels' contextual streaming to the biggest screen, on the existing adaptive streaming and creative-management foundation.";
const card2Bullets = [
  'VAST ad-serving endpoint for CTV ad servers and players',
  'Client-side playback and server-side ad insertion',
  'Publisher-direct or programmatic CTV campaigns',
  'Impressions, quartiles, completions and errors',
  'OMID verification where supported',
];

export default function PlayerSection() {
  return (
    <Root>
      <Inner id="player">
        <Eyebrow>The Pixels Streaming Platform</Eyebrow>
        <Headline>one streaming platform, relevant on every screen</Headline>
        <Cards>
          <Card>
            <CardBar>
              {CARD_BAR_SEGMENTS.web.map((segment) => (
                <CardBarSegment
                  key={segment.background}
                  flexValue={segment.flexValue}
                  background={segment.background}
                />
              ))}
            </CardBar>
            <CardBody>
              <CardTitle>Web and app</CardTitle>
              <CardIntro>{card1Intro}</CardIntro>
              <Checklist>
                {card1Bullets.map((bullet) => (
                  <ChecklistItem key={bullet}>{bullet}</ChecklistItem>
                ))}
              </Checklist>
            </CardBody>
          </Card>
          <Card>
            <CardBar>
              {CARD_BAR_SEGMENTS.ctv.map((segment) => (
                <CardBarSegment
                  key={segment.background}
                  flexValue={segment.flexValue}
                  background={segment.background}
                />
              ))}
            </CardBar>
            <CardBody>
              <CardTitle>CTV</CardTitle>
              <CardIntro>{card2Intro}</CardIntro>
              <Checklist>
                {card2Bullets.map((bullet) => (
                  <ChecklistItem key={bullet}>{bullet}</ChecklistItem>
                ))}
              </Checklist>
            </CardBody>
          </Card>
        </Cards>
      </Inner>
    </Root>
  );
}
